const CAMEL_CASE_BOUNDARY_PATTERN = /([a-z0-9])([A-Z])/g
const HEX_COLOR_WITH_ALPHA_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})(?:[0-9a-f]{2})?$/i
const RGB_COLOR_CHANNELS_PATTERN = /^rgba?\(\s*(\d+(?:\.\d+)?)\s*[, ]\s*(\d+(?:\.\d+)?)\s*[, ]\s*(\d+(?:\.\d+)?)/i

export interface BannerConfig {
  imageUrl: string
  linkUrl?: string
  altText?: string
}

export interface TypographyToken {
  fontSize: string
  fontSizeDesktop?: string
  fontWeight: string
  fontFamily: string
  fontStyle: string
  lineHeight: string
  lineHeightDesktop?: string
  letterSpacing: string
  textTransform: string
}

export interface BrandingAssets {
  mediaPlayIconUrl: string
  mediaPauseIconUrl: string
  mediaMutedIconUrl: string
  shareIconUrl: string
  copyCountIconUrl: string
  betslipMarkerIconUrl: string
}

export interface BetslipPresentation {
  variant: 'rows' | 'panel'
  labelTemplate: string
  parlayLabelTemplate: string
  showSingleLabel: boolean
  showToplineEvent: boolean
  showGroupLabel: boolean
  showLegEvent: boolean
  connectMultipleSingles: boolean
  badgePosition: 'after-label' | 'before-label'
  oddsPosition: 'trailing' | 'inline'
}

export interface BrandingPresentation {
  showFollowButton: boolean
  shareLabelMode: 'responsive' | 'always' | 'hidden'
  betslip: BetslipPresentation
}

export interface LayoutTokens {
  feed: Record<string, string>
  card: Record<string, string>
  header: Record<string, string>
  media: Record<string, string>
  betslip: Record<string, string>
  cta: Record<string, string>
  tags: Record<string, string>
  compactCard: Record<string, string>
  banner: Record<string, string>
}

export interface BrandingEffects {
  cardShadow: string
  cardHoverShadow: string
  cardHoverLift: string
  ctaShadow: string
  avatarShadow: string
  mediaControlBackdrop: string
  mediaControlHoverBackdrop: string
  playOverlayBackdrop: string
  compactDrawerShadow: string
  compactBackdrop: string
  overlayTextShadow: string
}

export interface BrandingComponents {
  followButton: Record<string, string>
  promotedBadge: Record<string, string>
  betslip: Record<string, string>
  statusBadges: Record<string, string>
  cta: Record<string, string>
  prepackActions: Record<string, string>
  prepackOdds: Record<string, string>
  shareButton: Record<string, string>
  copyCount: Record<string, string>
  compactCard: Record<string, string>
  feedStates: Record<string, string>
  surfaceHeading: Record<string, string>
  winningTicket: WinningTicketTokens
  crowdRecommendation: GeneratedCardTokens
  personalizedTicket: GeneratedCardTokens
}

/**
 * Winning-ticket card look and copy. Colors, radius and weight become CSS
 * variables; text and icon URLs are read from the resolved config.
 */
export interface WinningTicketTokens extends Record<string, string> {
  /** Band label, e.g. "WINNER". */
  headerText: string
  /** Band icon; empty uses the built-in trophy, tinted by headerIconColor. */
  headerIconUrl: string
  /** Header avatar override; empty uses the persona's avatar. */
  categoryIconUrl: string
  /** Feed brand credited on cards with no creator ("Banter"); empty hides it. */
  feedCreditName: string
}

/** A generated post's look on the standard card. '' = inherit (no CSS variable, no badge). */
export interface GeneratedCardTokens extends Record<string, string> {
  /** Header badge label next to the author name; empty shows no badge. */
  badgeText: string
  /** Author name shown on cards of this origin; empty uses the creator's name. */
  personaName: string
  /** Author avatar URL shown on cards of this origin; empty uses the creator's avatar. */
  personaAvatarUrl: string
  badgeBackground: string
  badgeColor: string
  cardBackground: string
  /** Card border and the personalized accent stripe. */
  borderColor: string
  textColor: string
  oddsColor: string
  ctaBackground: string
  ctaColor: string
  ctaBorderRadius: string
}

export type GeneratedCardGroup = 'crowdRecommendation' | 'personalizedTicket'
export type GeneratedCardOrigin = 'personalized_recommendation' | 'personalized_ticket'

/** The style tokens of a generated card group (everything but the badgeText, personaName and personaAvatarUrl copy). */
export const GENERATED_CARD_TOKEN_KEYS = [
  'badgeBackground', 'badgeColor', 'cardBackground', 'borderColor', 'textColor',
  'oddsColor', 'ctaBackground', 'ctaColor', 'ctaBorderRadius',
] as const satisfies readonly (keyof GeneratedCardTokens)[]

/** Which branding group styles each generated content origin. */
export const GENERATED_CARD_ORIGIN_GROUPS: Record<GeneratedCardOrigin, GeneratedCardGroup> = {
  personalized_recommendation: 'crowdRecommendation',
  personalized_ticket: 'personalizedTicket',
}

function emptyGeneratedCardTokens(): GeneratedCardTokens {
  return {
    badgeText: '',
    personaName: '',
    personaAvatarUrl: '',
    badgeBackground: '',
    badgeColor: '',
    cardBackground: '',
    borderColor: '',
    textColor: '',
    oddsColor: '',
    ctaBackground: '',
    ctaColor: '',
    ctaBorderRadius: '',
  }
}

export interface BrandingTypography {
  username: TypographyToken
  timestamp: TypographyToken
  body: TypographyToken
  betLabel: TypographyToken
  odds: TypographyToken
  legEvent: TypographyToken
  legMarket: TypographyToken
  legSelection: TypographyToken
  cta: TypographyToken
  copyCount: TypographyToken
  tag: TypographyToken
  badge: TypographyToken
  compactAvatarName: TypographyToken
}

export interface BrandingConfig {
  colors: Record<string, string>
  gradients: Record<string, string>
  fonts: Record<string, string>
  fontUrls: string[]
  buttonText: string
  copyCountLabel: string
  logoUrl: string
  borderWidth: string
  darkMode: boolean
  banner: BannerConfig
  assets: BrandingAssets
  presentation: BrandingPresentation
  layout: LayoutTokens
  typography: BrandingTypography
  effects: BrandingEffects
  components: BrandingComponents
}

type RawBrandingValue = null | undefined | string | number | boolean | RawBrandingValue[] | { [key: string]: RawBrandingValue }

// Operators send sparse overrides; every nested token group is optional.
type DeepPartial<T> = T extends (infer U)[] ? U[] : T extends object ? { [K in keyof T]?: DeepPartial<T[K]> } : T
export type DeepReadonly<T> = T extends (infer U)[] ? ReadonlyArray<DeepReadonly<U>> : T extends object ? { readonly [K in keyof T]: DeepReadonly<T[K]> } : T

export type RawBrandingConfig = DeepPartial<BrandingConfig> & {
  [key: string]: RawBrandingValue
}

const DEFAULT_TYPOGRAPHY: TypographyToken = {
  fontSize: '14px',
  fontWeight: '400',
  fontFamily: 'inherit',
  fontStyle: 'normal',
  lineHeight: '1.4',
  letterSpacing: '0px',
  textTransform: 'none',
}

export const DEFAULT_BRANDING: BrandingConfig = {
  colors: {
    background: '#1A1D2E',
    surface: '#1A1D2E',
    primary: '#3B82F6',
    secondary: '#60A5FA',
    highlight: '#93C5FD',
    heading: '#FFFFFF',
    subheading: '#94A3B8',
    error: '#EF4444',
    success: '#A4FFDC',
    warning: '#F59E0B',
    buttonTextPrimary: '#FFFFFF',
    buttonTextSecondary: '#1A1D2E',
    borderColor: '#2D3348',
  },
  gradients: {
    primary: 'linear-gradient(309deg, #1E3A5F 31%, #3B82F6 73%)',
    secondary: 'linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)',
    shared: 'linear-gradient(180deg, rgba(26,29,46,0.8) 0%, rgba(26,29,46,0.95) 100%)',
  },
  fonts: { primary: 'Inter, sans-serif' },
  fontUrls: ['https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap'],
  buttonText: 'Add to Betslip',
  copyCountLabel: 'copied',
  logoUrl: '',
  borderWidth: '0px',
  darkMode: true,
  banner: {
    imageUrl: '',
    linkUrl: '',
    altText: '',
  },
  assets: {
    mediaPlayIconUrl: '',
    mediaPauseIconUrl: '',
    mediaMutedIconUrl: '',
    shareIconUrl: '',
    copyCountIconUrl: '',
    betslipMarkerIconUrl: '',
  },
  presentation: {
    showFollowButton: true,
    shareLabelMode: 'hidden',
    betslip: {
      variant: 'rows',
      labelTemplate: '',
      parlayLabelTemplate: '',
      showSingleLabel: true,
      showToplineEvent: false,
      showGroupLabel: true,
      showLegEvent: true,
      connectMultipleSingles: false,
      badgePosition: 'after-label',
      oddsPosition: 'trailing',
    },
  },
  layout: {
    feed: {
      maxWidth: '520px',
      paddingX: '20px',
      paddingY: '20px',
      cardGap: '16px',
      bannerGap: '16px',
      endLineWidth: '100px',
      loadingPaddingX: '16px',
      loadingPaddingY: '64px',
    },
    card: {
      borderWidth: '0px',
      borderRadius: '12px',
      headerPaddingX: '14px',
      headerPaddingY: '12px',
      contentPaddingX: '14px',
      contentPaddingY: '10px',
      contentPaddingTop: '10px',
      contentPaddingBottom: '10px',
    },
    header: {
      gap: '12px',
      avatarSize: '44px',
    },
    media: {
      gridGap: '2px',
      aspectRatio: 'auto',
      objectFit: 'contain',
      objectPosition: 'center',
      playOverlaySize: '56px',
      controlButtonSize: '36px',
      controlGap: '8px',
      controlInset: '12px',
      videoMaxHeight: '500px',
    },
    betslip: {
      outerPaddingX: '0px',
      outerPaddingY: '0px',
      panelPaddingX: '0px',
      panelPaddingY: '0px',
      panelBorderWidth: '0px',
      panelBorderRadius: '0px',
      panelMinHeight: '0px',
      panelGap: '0px',
      rowGap: '0px',
      topLineGap: '0px',
      metaGap: '6px',
      topLinePaddingX: '14px',
      topLinePaddingY: '8px',
      legPaddingX: '10px',
      legPaddingY: '8px',
      legGap: '10px',
      connectorWidth: '2px',
      connectorDotSize: '8px',
      connectorDotBorderWidth: '2px',
      connectorCoreSize: '3px',
      connectorHaloWidth: '2px',
      sgpLabelPaddingX: '6px',
      sgpLabelPaddingY: '2px',
      sgpLabelRadius: '6px',
      sgpAccentWidth: '3px',
    },
    cta: {
      actionsPaddingX: '14px',
      actionsPaddingY: '10px',
      buttonHeight: 'auto',
      copyCountPaddingTop: '8px',
      copyCountPaddingBottom: '8px',
      paddingX: '20px',
      paddingY: '12px',
      borderRadius: '12px',
      buttonTopRadius: '0px',
      warningPaddingX: '14px',
      warningPaddingY: '6px',
    },
    tags: {
      inlineGap: '6px',
      inlinePaddingX: '14px',
      inlinePaddingY: '4px',
      headerGap: '4px',
      headerMarginLeft: '8px',
      chipGap: '4px',
      chipPaddingX: '8px',
      chipPaddingY: '4px',
      chipRadius: '9999px',
    },
    compactCard: {
      widthMobile: '300px',
      heightMobile: '260px',
      widthDesktop: '400px',
      heightDesktop: '315px',
      borderWidth: '0px',
      borderRadius: '12px',
      avatarTop: '8px',
      avatarLeft: '8px',
      avatarGap: '6px',
      avatarSize: '28px',
      legsPaddingX: '10px',
      legsPaddingY: '6px',
      actionPaddingX: '10px',
      actionPaddingY: '8px',
      ctaRadius: '6px',
      drawerRadius: '12px',
      drawerMaxHeight: '200px',
      drawerPaddingX: '12px',
      drawerPaddingY: '8px',
    },
    banner: {
      borderRadius: '12px',
      spacingBottom: '16px',
    },
  },
  typography: {
    username: {
      ...DEFAULT_TYPOGRAPHY,
      fontSize: '15px',
      fontWeight: '600',
      lineHeight: '1.2',
      letterSpacing: '0px',
      textTransform: 'none',
    },
    timestamp: {
      ...DEFAULT_TYPOGRAPHY,
      fontSize: '13px',
      fontWeight: '400',
      lineHeight: '1.2',
      letterSpacing: '0px',
      textTransform: 'none',
    },
    body: {
      ...DEFAULT_TYPOGRAPHY,
      fontSize: '15px',
      fontWeight: '400',
      lineHeight: '1.5',
      letterSpacing: '0px',
      textTransform: 'none',
    },
    betLabel: {
      ...DEFAULT_TYPOGRAPHY,
      fontSize: '11px',
      fontWeight: '700',
      lineHeight: '1.2',
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    },
    odds: {
      ...DEFAULT_TYPOGRAPHY,
      fontSize: '16px',
      fontWeight: '800',
      lineHeight: '1.2',
      letterSpacing: '0px',
      textTransform: 'none',
    },
    legEvent: {
      ...DEFAULT_TYPOGRAPHY,
      fontSize: '12px',
      fontWeight: '400',
      lineHeight: '1.2',
      letterSpacing: '0px',
      textTransform: 'none',
    },
    legMarket: {
      ...DEFAULT_TYPOGRAPHY,
      fontSize: '11px',
      fontWeight: '400',
      lineHeight: '1.2',
      letterSpacing: '0px',
      textTransform: 'none',
    },
    legSelection: {
      ...DEFAULT_TYPOGRAPHY,
      fontSize: '14px',
      fontWeight: '600',
      lineHeight: '1.2',
      letterSpacing: '0px',
      textTransform: 'none',
    },
    cta: {
      ...DEFAULT_TYPOGRAPHY,
      fontSize: '15px',
      fontWeight: '700',
      lineHeight: '1.2',
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    },
    copyCount: {
      ...DEFAULT_TYPOGRAPHY,
      fontSize: '12px',
      fontWeight: '600',
      lineHeight: '1.2',
      letterSpacing: '0px',
      textTransform: 'none',
    },
    tag: {
      ...DEFAULT_TYPOGRAPHY,
      fontSize: '10px',
      fontWeight: '600',
      lineHeight: '1',
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    },
    badge: {
      ...DEFAULT_TYPOGRAPHY,
      fontSize: '10px',
      fontWeight: '700',
      lineHeight: '1.2',
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    },
    compactAvatarName: {
      ...DEFAULT_TYPOGRAPHY,
      fontSize: '12px',
      fontWeight: '600',
      lineHeight: '1.2',
      letterSpacing: '0px',
      textTransform: 'none',
    },
  },
  effects: {
    cardShadow: '0 0 0 rgba(0, 0, 0, 0)',
    cardHoverShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
    cardHoverLift: '2px',
    ctaShadow: 'none',
    avatarShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
    mediaControlBackdrop: 'rgba(0, 0, 0, 0.5)',
    mediaControlHoverBackdrop: 'rgba(0, 0, 0, 0.7)',
    playOverlayBackdrop: 'rgba(0, 0, 0, 0.5)',
    compactDrawerShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
    compactBackdrop: 'rgba(0, 0, 0, 0.4)',
    overlayTextShadow: '0 1px 4px rgba(0, 0, 0, 0.7)',
  },
  components: {
    prepackActions: {
      background: 'var(--feed-surface)',
      borderColor: 'var(--feed-border-color)',
    },
    prepackOdds: {
      background: 'color-mix(in srgb, var(--feed-heading) 8%, transparent)',
      color: 'var(--feed-heading)',
      borderColor: 'transparent',
      borderWidth: '0px',
      borderRadius: '6px',
      paddingX: '8px',
      paddingY: '4px',
    },
    followButton: {
      paddingX: '12px',
      paddingY: '6px',
      borderRadius: '999px',
      fontSize: '12px',
      fontWeight: '700',
      followingBackgroundDark: 'rgba(255, 255, 255, 0.08)',
      followingBackgroundLight: 'rgba(0, 0, 0, 0.06)',
    },
    promotedBadge: {
      paddingX: '0',
      paddingY: '0',
      borderRadius: '0',
      fontSize: '10px',
      fontWeight: '600',
      letterSpacing: '0.3px',
      backgroundDark: 'transparent',
      backgroundLight: 'transparent',
      colorDark: '#94A3B8',
      colorLight: '#64748B',
    },
    betslip: {
      panelBackground: 'transparent',
      panelBorderColor: 'transparent',
      rowEvenDark: 'rgba(255, 255, 255, 0.03)',
      rowOddDark: 'rgba(255, 255, 255, 0.06)',
      rowEvenLight: 'rgba(0, 0, 0, 0.02)',
      rowOddLight: 'rgba(0, 0, 0, 0.04)',
      connectorLineColor: 'color-mix(in srgb, var(--feed-subheading) 42%, transparent)',
      connectorMarkerColor: 'auto',
      connectorMarkerFill: 'var(--feed-surface)',
      sgpLabelBackground: '#93C5FD',
      sgpLabelColor: '#0F172A',
      liveBadgeBackground: '#EF4444',
      liveBadgeColor: '#FFFFFF',
      warningBackground: 'rgba(245, 158, 11, 0.1)',
    },
    statusBadges: {
      wonBackground: '#A4FFDC',
      wonColor: '#0B3D2E',
      lostBackground: '#FE9EC9',
      lostColor: '#5D1A3A',
      suspendedBackground: '#FFD700',
      suspendedColor: '#3D3000',
      closedBackgroundDark: 'rgba(255, 255, 255, 0.2)',
      closedBackgroundLight: 'rgba(0, 0, 0, 0.08)',
      closedColor: '#94A3B8',
    },
    cta: {
      addedBackground: 'var(--feed-component-cta-active-bg, var(--feed-primary, #3B82F6))',
      addedColor: 'var(--feed-component-cta-active-color, var(--feed-btn-text-primary, #FFFFFF))',
      borderBottomWidth: '0px',
      disabledOpacity: '0.5',
    },
    shareButton: {
      background: 'color-mix(in srgb, var(--feed-surface, #1a1d2e) 88%, transparent)',
      color: 'var(--feed-subheading, #94a3b8)',
      borderColor: 'color-mix(in srgb, var(--feed-border-color, #2d3348) 78%, transparent)',
      borderWidth: '1px',
      borderRadius: '999px',
      paddingX: '9px',
      paddingY: '5px',
      gap: '5px',
      minHeight: '30px',
      fontSize: '11px',
      fontWeight: '700',
      lineHeight: '1',
      iconSize: '15px',
    },
    copyCount: {
      iconColorDark: '#FF9F1C',
      iconColorLight: '#FF9F1C',
    },
    compactCard: {
      background: 'var(--feed-surface, var(--feed-bg, #1A1D2E))',
      borderColor: 'var(--feed-border-color, transparent)',
      boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
      overlayTextColor: '#FFFFFF',
      avatarBorderColor: 'rgba(255, 255, 255, 0.3)',
      avatarBorderWidth: '2px',
      expandBackground: 'rgba(255, 255, 255, 0.08)',
      expandHoverBackground: 'rgba(255, 255, 255, 0.15)',
      drawerCloseBackground: 'rgba(255, 255, 255, 0.1)',
      drawerCloseHoverBackground: 'rgba(255, 255, 255, 0.2)',
      drawerEvenRowBackground: 'rgba(255, 255, 255, 0.03)',
    },
    surfaceHeading: {
      background: '#1A1D2E',
      color: '#FFFFFF',
      highlightColor: '#60A5FA',
      borderRadius: '8px',
      paddingX: '16px',
      paddingY: '14px',
      marginBottom: '16px',
    },
    feedStates: {
      loadingTextColor: '#94A3B8',
      errorTitleColor: '#EF4444',
      emptyHeadingColor: '#FFFFFF',
      emptySubheadingColor: '#94A3B8',
      retryButtonRadius: '8px',
    },
    winningTicket: {
      headerText: 'WINNER',
      headerIconUrl: '',
      categoryIconUrl: '',
      feedCreditName: '',
      headerBackground: '#0B1E3F',
      headerIconColor: '#F5B400',
      bodyBackground: '#1B4FBF',
      textColor: '#FFFFFF',
      successColor: '#22C55E',
      oddsColor: '#FFFFFF',
      dividerColor: 'rgba(255, 255, 255, 0.35)',
      ctaBackground: '#FFFFFF',
      ctaColor: '#1B4FBF',
      ctaBorderRadius: '999px',
      ctaFontWeight: '700',
    },
    crowdRecommendation: emptyGeneratedCardTokens(),
    personalizedTicket: emptyGeneratedCardTokens(),
  },
}

const LEGACY_COLOR_KEYS = [
  'background',
  'primary',
  'secondary',
  'highlight',
  'heading',
  'subheading',
  'error',
  'success',
  'warning',
  'buttonTextPrimary',
  'buttonTextSecondary',
  'borderColor',
]

// CSS variable names follow a systematic `--feed-<namespace>-<kebab(name)>`
// pattern per branding namespace. Rather than hand-writing all ~260 pairs,
// each namespace lists its token names and a shared generator derives the
// CSS variable name; CSS_VAR_OVERRIDES captures the handful of names that
// deviate from the systematic pattern (abbreviations, dropped words, etc).

const KEBAB_ABBREVIATIONS: Record<string, string> = {
  background: 'bg',
}

function kebabCase(name: string): string {
  return name.replace(CAMEL_CASE_BOUNDARY_PATTERN, '$1-$2').toLowerCase()
}

function kebabCaseAbbrev(name: string): string {
  return kebabCase(name)
    .split('-')
    .map((segment) => KEBAB_ABBREVIATIONS[segment] || segment)
    .join('-')
}

const LAYOUT_SECTION_ALIASES: Record<string, string> = {
  compactCard: 'compact',
}

const COMPONENT_SECTION_ALIASES: Record<string, string> = {
  followButton: 'follow',
  shareButton: 'share',
  promotedBadge: 'promoted',
  statusBadges: 'status',
  compactCard: 'compact',
  surfaceHeading: 'surface-heading',
  feedStates: 'feed',
  crowdRecommendation: 'crowd-recommendation',
  personalizedTicket: 'personalized-ticket',
}

const TYPOGRAPHY_PROP_ALIASES: Record<string, string> = {
  fontSize: 'size',
  fontSizeDesktop: 'size-desktop',
  fontWeight: 'weight',
  fontFamily: 'family',
  fontStyle: 'style',
  textTransform: 'transform',
  lineHeight: 'line-height',
  lineHeightDesktop: 'line-height-desktop',
  letterSpacing: 'letter-spacing',
}

const COLOR_TOKENS = [
  'background', 'surface', 'primary', 'secondary', 'highlight', 'heading',
  'subheading', 'error', 'success', 'warning', 'buttonTextPrimary',
  'buttonTextSecondary', 'borderColor',
]

const GRADIENT_TOKENS = ['primary', 'secondary', 'shared']

const FONT_TOKENS = ['primary']

const EFFECT_TOKENS = [
  'cardShadow', 'cardHoverShadow', 'cardHoverLift', 'avatarShadow',
  'ctaShadow',
  'mediaControlBackdrop', 'mediaControlHoverBackdrop', 'playOverlayBackdrop',
  'compactDrawerShadow', 'compactBackdrop', 'overlayTextShadow',
]

const TYPOGRAPHY_COMPONENTS = [
  'username', 'timestamp', 'body', 'betLabel', 'odds', 'legEvent',
  'legMarket', 'legSelection', 'cta', 'copyCount', 'tag', 'badge',
  'compactAvatarName',
]

const TYPOGRAPHY_PROPS = [
  'fontSize', 'fontSizeDesktop', 'fontWeight', 'fontFamily', 'fontStyle',
  'lineHeight', 'lineHeightDesktop', 'letterSpacing', 'textTransform',
]

const LAYOUT_TOKENS: Record<string, string[]> = {
  feed: [
    'maxWidth', 'maxWidthDesktop', 'marginInline', 'paddingX', 'paddingY', 'cardGap',
    'bannerGap', 'endLineWidth', 'loadingPaddingX', 'loadingPaddingY',
  ],
  card: [
    'borderWidth', 'borderRadius', 'headerPaddingX', 'headerPaddingY',
    'contentPaddingX', 'contentPaddingY', 'contentPaddingTop',
    'contentPaddingBottom',
  ],
  header: ['gap', 'avatarSize'],
  media: [
    'gridGap', 'aspectRatio', 'objectFit', 'objectPosition', 'playOverlaySize',
    'controlButtonSize', 'controlGap', 'controlInset', 'videoMaxHeight',
  ],
  betslip: [
    'outerPaddingX', 'outerPaddingY', 'panelPaddingX', 'panelPaddingY',
    'panelGap', 'panelBorderWidth', 'panelBorderRadius', 'panelMinHeight',
    'rowGap', 'topLineGap', 'metaGap',
    'topLinePaddingX', 'topLinePaddingY', 'legPaddingX', 'legPaddingY',
    'legGap', 'connectorWidth', 'connectorDotSize', 'connectorDotBorderWidth',
    'connectorCoreSize', 'connectorHaloWidth', 'sgpLabelPaddingX',
    'sgpLabelPaddingY', 'sgpLabelPaddingTop', 'sgpLabelPaddingBottom',
    'sgpLabelRadius', 'sgpAccentWidth',
  ],
  cta: [
    'actionsPaddingX', 'actionsPaddingY', 'buttonHeight',
    'copyCountPaddingTop', 'copyCountPaddingBottom', 'paddingX', 'paddingY',
    'borderRadius', 'buttonTopRadius', 'warningPaddingX', 'warningPaddingY',
  ],
  tags: [
    'inlineGap', 'inlinePaddingX', 'inlinePaddingY', 'headerGap',
    'headerMarginLeft', 'chipGap', 'chipPaddingX', 'chipPaddingY', 'chipRadius',
  ],
  compactCard: [
    'widthMobile', 'heightMobile', 'widthDesktop', 'heightDesktop',
    'borderWidth', 'borderRadius', 'avatarTop', 'avatarLeft', 'avatarGap',
    'avatarSize', 'legsPaddingX', 'legsPaddingY', 'actionPaddingX',
    'actionPaddingY', 'ctaRadius', 'drawerRadius', 'drawerMaxHeight',
    'drawerPaddingX', 'drawerPaddingY',
  ],
  banner: ['borderRadius', 'spacingBottom'],
}

const COMPONENT_TOKENS: Record<string, string[]> = {
  prepackActions: ['background', 'borderColor'],
  prepackOdds: ['background', 'color', 'borderColor', 'borderWidth', 'borderRadius', 'paddingX', 'paddingY'],
  followButton: [
    'paddingX', 'paddingY', 'borderRadius', 'fontSize', 'fontWeight',
    'followingBackgroundDark', 'followingBackgroundLight',
  ],
  promotedBadge: [
    'paddingX', 'paddingY', 'borderRadius', 'fontSize', 'fontWeight',
    'letterSpacing', 'backgroundDark', 'backgroundLight', 'colorDark', 'colorLight',
  ],
  betslip: [
    'panelBackground', 'panelBorderColor',
    'rowEvenDark', 'rowOddDark', 'rowEvenLight', 'rowOddLight',
    'connectorLineColor', 'connectorMarkerColor', 'connectorMarkerFill',
    'sgpLabelBackground', 'sgpLabelColor', 'sgpLabelFontFamily',
    'sgpLabelFontSize', 'sgpLabelFontWeight', 'sgpLabelFontStyle',
    'sgpLabelLineHeight', 'sgpLabelLetterSpacing', 'sgpLabelTextTransform',
    'sgpLabelSkew', 'liveBadgeBackground', 'liveBadgeColor', 'warningBackground',
  ],
  statusBadges: [
    'wonBackground', 'wonColor', 'lostBackground', 'lostColor',
    'suspendedBackground', 'suspendedColor', 'closedBackgroundDark',
    'closedBackgroundLight', 'closedColor',
  ],
  cta: [
    'background', 'color', 'borderWidth', 'borderColor', 'hoverBackground',
    'hoverBorderColor', 'hoverOpacity', 'activeBackground', 'activeColor',
    'activeBorderColor', 'addedBackground', 'addedColor', 'addedBorderColor',
    'borderBottomWidth', 'disabledOpacity',
  ],
  shareButton: [
    'background', 'color', 'borderColor', 'borderWidth', 'borderRadius',
    'paddingX', 'paddingY', 'gap', 'minHeight', 'fontSize', 'fontWeight',
    'lineHeight', 'iconSize',
  ],
  copyCount: [
    'backgroundDark', 'backgroundLight', 'colorDark', 'colorLight',
    'labelColorDark', 'labelColorLight', 'iconColorDark', 'iconColorLight',
    'borderColorDark', 'borderColorLight',
  ],
  compactCard: [
    'background', 'borderColor', 'boxShadow', 'overlayTextColor',
    'avatarBorderColor', 'avatarBorderWidth', 'expandBackground',
    'expandHoverBackground', 'drawerCloseBackground',
    'drawerCloseHoverBackground', 'drawerEvenRowBackground',
  ],
  surfaceHeading: [
    'background', 'color', 'highlightColor', 'borderRadius', 'paddingX',
    'paddingY', 'marginBottom',
  ],
  feedStates: [
    'loadingTextColor', 'errorTitleColor', 'emptyHeadingColor',
    'emptySubheadingColor', 'retryButtonRadius',
  ],
  // headerText, headerIconUrl, categoryIconUrl and feedCreditName are copy
  // and URLs, read by the card from the resolved config rather than CSS.
  winningTicket: [
    'headerBackground', 'headerIconColor', 'bodyBackground', 'textColor', 'successColor',
    'oddsColor', 'dividerColor', 'ctaBackground', 'ctaColor',
    'ctaBorderRadius', 'ctaFontWeight',
  ],
  // badgeText, personaName and personaAvatarUrl are copy; style tokens are emitted only when set.
  crowdRecommendation: [...GENERATED_CARD_TOKEN_KEYS],
  personalizedTicket: [...GENERATED_CARD_TOKEN_KEYS],
}

// Names whose generated CSS variable deviates from the systematic pattern.
const CSS_VAR_OVERRIDES: Record<string, string> = {
  'colors.buttonTextPrimary': '--feed-btn-text-primary',
  'colors.buttonTextSecondary': '--feed-btn-text-secondary',
  'layout.media.gridGap': '--feed-layout-media-gap',
  'layout.media.controlButtonSize': '--feed-layout-media-control-size',
  'layout.betslip.topLinePaddingX': '--feed-layout-betslip-topline-padding-x',
  'layout.betslip.topLinePaddingY': '--feed-layout-betslip-topline-padding-y',
  'components.followButton.followingBackgroundDark': '--feed-component-follow-bg-dark',
  'components.followButton.followingBackgroundLight': '--feed-component-follow-bg-light',
  'components.compactCard.background': '--feed-component-compact-background',
  'components.surfaceHeading.highlightColor': '--feed-component-surface-heading-highlight',
  'components.feedStates.retryButtonRadius': '--feed-component-feed-retry-radius',
}

function layoutVarName(section: string, name: string): string {
  const sectionKebab = LAYOUT_SECTION_ALIASES[section] || kebabCase(section)
  const nameKebab = name === 'borderRadius' ? 'radius' : kebabCaseAbbrev(name)
  return `--feed-layout-${sectionKebab}-${nameKebab}`
}

function componentVarName(section: string, prop: string): string {
  const sectionKebab = COMPONENT_SECTION_ALIASES[section] || kebabCase(section)
  const propKebab = prop === 'borderRadius' ? 'radius' : kebabCaseAbbrev(prop)
  return `--feed-component-${sectionKebab}-${propKebab}`
}

function typographyVarName(component: string, prop: string): string {
  const propKebab = TYPOGRAPHY_PROP_ALIASES[prop] || kebabCase(prop)
  return `--feed-typo-${kebabCase(component)}-${propKebab}`
}

function buildCssVarMap(): Record<string, string> {
  const map: Record<string, string> = {}
  const set = (path: string, cssVar: string) => {
    map[path] = CSS_VAR_OVERRIDES[path] || cssVar
  }

  for (const name of COLOR_TOKENS) set(`colors.${name}`, `--feed-${kebabCaseAbbrev(name)}`)
  for (const name of GRADIENT_TOKENS) set(`gradients.${name}`, `--feed-gradient-${kebabCaseAbbrev(name)}`)
  for (const name of FONT_TOKENS) set(`fonts.${name}`, `--feed-font-${kebabCaseAbbrev(name)}`)
  for (const name of EFFECT_TOKENS) set(`effects.${name}`, `--feed-effect-${kebabCaseAbbrev(name)}`)

  for (const [section, names] of Object.entries(LAYOUT_TOKENS)) {
    for (const name of names) set(`layout.${section}.${name}`, layoutVarName(section, name))
  }

  for (const component of TYPOGRAPHY_COMPONENTS) {
    for (const prop of TYPOGRAPHY_PROPS) {
      set(`typography.${component}.${prop}`, typographyVarName(component, prop))
    }
  }

  for (const [section, props] of Object.entries(COMPONENT_TOKENS)) {
    for (const prop of props) set(`components.${section}.${prop}`, componentVarName(section, prop))
  }

  return map
}

const CSS_VAR_MAP: Record<string, string> = buildCssVarMap()

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function deepClone<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value)
  }
  return JSON.parse(JSON.stringify(value)) as T
}

function deepMerge<T>(base: T, override?: unknown): T {
  if (Array.isArray(base)) {
    return (Array.isArray(override) ? [...override] : [...base]) as T
  }

  if (!isPlainObject(base)) {
    return (override ?? base) as T
  }

  const result: Record<string, unknown> = { ...base }
  if (!isPlainObject(override)) {
    return result as T
  }

  for (const [key, value] of Object.entries(override)) {
    const current = result[key]
    if (Array.isArray(current)) {
      result[key] = Array.isArray(value) ? [...value] : [...current]
      continue
    }
    if (isPlainObject(current)) {
      result[key] = deepMerge(current, value)
      continue
    }
    result[key] = value
  }

  return result as T
}

// Expand shorthand in the authored layer, before inherited defaults or
// organization values can be mistaken for explicit edge overrides.
function normalizeCardLayoutAliases(layer: Record<string, unknown>): Record<string, unknown> {
  const layout = isPlainObject(layer.layout) ? layer.layout : {}
  const card = isPlainObject(layout.card) ? layout.card : {}
  const aliases: Record<string, unknown> = {
    borderWidth: layer.borderWidth,
    contentPaddingTop: card.contentPaddingY,
    contentPaddingBottom: card.contentPaddingY,
  }
  let normalizedCard: Record<string, unknown> | undefined
  for (const [key, value] of Object.entries(aliases)) {
    if (typeof value === 'string' && value && !Object.hasOwn(card, key)) {
      normalizedCard ??= { ...card }
      normalizedCard[key] = value
    }
  }
  return normalizedCard ? { ...layer, layout: { ...layout, card: normalizedCard } } : layer
}

function getByPath(source: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((current, part) => {
    if (!isPlainObject(current)) return undefined
    return current[part]
  }, source)
}

const SAFE_CONNECTOR_MARKER_COLOR = '#FFB800'

interface RGBColor {
  red: number
  green: number
  blue: number
}

function parseHexColor(value: string): RGBColor | null {
  const match = value.trim().match(HEX_COLOR_WITH_ALPHA_PATTERN)
  if (!match) return null

  const hex = match[1].length === 3
    ? match[1].split('').map((digit) => `${digit}${digit}`).join('')
    : match[1]

  return {
    red: Number.parseInt(hex.slice(0, 2), 16),
    green: Number.parseInt(hex.slice(2, 4), 16),
    blue: Number.parseInt(hex.slice(4, 6), 16),
  }
}

function parseRgbColor(value: string): RGBColor | null {
  const match = value.trim().match(
    RGB_COLOR_CHANNELS_PATTERN,
  )
  if (!match) return null

  return {
    red: Math.min(255, Number(match[1])),
    green: Math.min(255, Number(match[2])),
    blue: Math.min(255, Number(match[3])),
  }
}

function parseColor(value: unknown): RGBColor | null {
  if (typeof value !== 'string') return null
  return parseHexColor(value) || parseRgbColor(value)
}

function colorHueAndSaturation(color: RGBColor): { hue: number; saturation: number } {
  const red = color.red / 255
  const green = color.green / 255
  const blue = color.blue / 255
  const maximum = Math.max(red, green, blue)
  const minimum = Math.min(red, green, blue)
  const delta = maximum - minimum
  const lightness = (maximum + minimum) / 2

  if (delta === 0) return { hue: 0, saturation: 0 }

  let hue = 0
  if (maximum === red) {
    hue = 60 * (((green - blue) / delta) % 6)
  } else if (maximum === green) {
    hue = 60 * (((blue - red) / delta) + 2)
  } else {
    hue = 60 * (((red - green) / delta) + 4)
  }

  return {
    hue: hue < 0 ? hue + 360 : hue,
    saturation: delta / (1 - Math.abs((2 * lightness) - 1)),
  }
}

function isWinLossColor(color: RGBColor): boolean {
  const { hue, saturation } = colorHueAndSaturation(color)
  if (saturation < 0.25) return false

  const isRed = hue <= 25 || hue >= 330
  const isGreen = hue >= 75 && hue <= 175
  return isRed || isGreen
}

function relativeLuminance(color: RGBColor): number {
  const channels = [color.red, color.green, color.blue].map((channel) => {
    const normalized = channel / 255
    return normalized <= 0.04045
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4
  })
  return (0.2126 * channels[0]) + (0.7152 * channels[1]) + (0.0722 * channels[2])
}

function colorContrast(first: RGBColor, second: RGBColor): number {
  const lighter = Math.max(relativeLuminance(first), relativeLuminance(second))
  const darker = Math.min(relativeLuminance(first), relativeLuminance(second))
  return (lighter + 0.05) / (darker + 0.05)
}

function automaticConnectorMarkerColor(branding: BrandingConfig): string {
  const markerFill = parseColor(branding.components.betslip.connectorMarkerFill)
    || parseColor(branding.colors.surface)
    || parseColor(branding.colors.background)

  for (const candidate of [
    branding.colors.highlight,
    branding.colors.primary,
    branding.colors.secondary,
  ]) {
    const parsed = parseColor(candidate)
    if (!parsed || isWinLossColor(parsed)) continue
    if (markerFill && colorContrast(parsed, markerFill) < 1.6) continue
    return candidate
  }

  return SAFE_CONNECTOR_MARKER_COLOR
}


// Organizations onboarded by migration (Potawatomi's 000177) store the
// original snake_case `primary_color` and `dark_mode`. The current keys win
// when both are present.
function applySnakeCaseBranding(raw: Record<string, unknown>): void {
  const colors = isPlainObject(raw.colors) ? raw.colors : {}
  raw.colors = colors
  const primary = raw.primary_color
  if (typeof primary === 'string' && primary.trim() !== '' && !colors.primary) {
    colors.primary = primary.trim()
  }
  if (typeof raw.dark_mode === 'boolean' && typeof raw.darkMode !== 'boolean') {
    raw.darkMode = raw.dark_mode
  }
}

export function resolveBranding(rawBranding?: RawBrandingConfig | null): BrandingConfig {
  const normalized = deepClone(DEFAULT_BRANDING) as unknown as Record<string, unknown>
  const raw = isPlainObject(rawBranding) ? deepClone(rawBranding) as Record<string, unknown> : {}

  for (const key of LEGACY_COLOR_KEYS) {
    if (!raw.colors || !isPlainObject(raw.colors)) {
      raw.colors = {}
    }
    if ((raw as Record<string, unknown>)[key] && !(raw.colors as Record<string, unknown>)[key]) {
      ;(raw.colors as Record<string, unknown>)[key] = (raw as Record<string, unknown>)[key]
    }
  }
  applySnakeCaseBranding(raw)


  const merged = deepMerge(normalized, normalizeCardLayoutAliases(raw)) as unknown as BrandingConfig
  const rawColors = isPlainObject(raw.colors) ? raw.colors : null
  if (!rawColors || typeof rawColors.surface !== 'string' || rawColors.surface.trim() === '') {
    merged.colors.surface = merged.colors.background
  }

  if (!merged.fontUrls?.length) {
    merged.fontUrls = [...DEFAULT_BRANDING.fontUrls]
  }

  merged.banner = {
    ...DEFAULT_BRANDING.banner,
    ...(merged.banner || {}),
  }

  merged.borderWidth = merged.layout.card.borderWidth || merged.borderWidth || DEFAULT_BRANDING.borderWidth
  merged.fonts = {
    ...DEFAULT_BRANDING.fonts,
    ...(merged.fonts || {}),
  }

  for (const [key, value] of Object.entries(DEFAULT_BRANDING.typography)) {
    merged.typography[key as keyof BrandingTypography] = deepMerge(
      value,
      merged.typography?.[key as keyof BrandingTypography]
    )
  }

  return merged
}

/**
 * resolveBrandingWithOverrides deep-merges a partial surface-level
 * `theme_overrides` object into an already-resolved org branding. Missing
 * or empty overrides produce the input unchanged (zero behavior change).
 * Used by ConfigContext when a surface has `theme_overrides` on the
 * fetched config — the surface can tweak any subset of branding tokens
 * without having to restate the full config shape.
 */
export function resolveBrandingWithOverrides(
  base: BrandingConfig,
  overrides?: Record<string, unknown> | null,
): BrandingConfig {
  if (!overrides || Object.keys(overrides).length === 0) {
    return base
  }
  const merged = deepMerge(base, normalizeCardLayoutAliases(overrides))
  merged.borderWidth = merged.layout.card.borderWidth || merged.borderWidth
  const overrideColors = isPlainObject(overrides.colors) ? overrides.colors : null
  if (
    overrideColors &&
    typeof overrideColors.background === 'string' &&
    (typeof overrideColors.surface !== 'string' || overrideColors.surface.trim() === '')
  ) {
    merged.colors.surface = merged.colors.background
  }
  return merged
}

export function brandingToCssVars(branding: BrandingConfig, authoredTokens?: DeepReadonly<RawBrandingConfig>): Record<string, string> {
  const vars: Record<string, string> = {}
  const authored = authoredTokens ? normalizeCardLayoutAliases(authoredTokens as Record<string, unknown>) : undefined

  for (const [path, cssVar] of Object.entries(CSS_VAR_MAP)) {
    if (authored && getByPath(authored, path) === undefined) continue
    const rawValue = getByPath(branding as unknown as Record<string, unknown>, path)
    const value = path === 'components.betslip.connectorMarkerColor'
      && typeof rawValue === 'string'
      && rawValue.trim().toLowerCase() === 'auto'
      ? automaticConnectorMarkerColor(branding)
      : rawValue
    if (value == null) continue
    if (isGeneratedCardPath(path) && String(value).trim() === '') continue
    vars[cssVar] = String(value)
  }

  return vars
}

function isGeneratedCardPath(path: string): boolean {
  return path.startsWith('components.crowdRecommendation.') || path.startsWith('components.personalizedTicket.')
}

// Card variables each token overrides. `--feed-card-*` are scoped variables the
// card CSS reads with its previous value as fallback; 'group' = the group's own
// `--feed-component-<group>-badge-*`.
const GENERATED_CARD_STYLE_VARS: Record<(typeof GENERATED_CARD_TOKEN_KEYS)[number], string[] | 'group'> = {
  badgeBackground: 'group',
  badgeColor: 'group',
  cardBackground: ['--feed-surface', '--feed-component-compact-background'],
  // The accent also colours the editorial eyebrow (the card's accent label)
  // through its own variable, so `--feed-primary` accents elsewhere keep theirs.
  borderColor: ['--feed-border-color', '--feed-component-compact-border-color', '--feed-card-accent-color', '--feed-card-eyebrow-color'],
  // Primary and secondary text: title, author and legs read the heading
  // colour; subtitle, body and rationale read the subheading colour.
  textColor: ['--feed-heading', '--feed-subheading'],
  oddsColor: ['--feed-card-odds-color'],
  ctaBackground: ['--feed-component-cta-bg'],
  ctaColor: ['--feed-component-cta-color'],
  ctaBorderRadius: ['--feed-layout-cta-radius', '--feed-layout-compact-cta-radius'],
}

/** The branding group that styles a content origin, or null for any other origin. */
export function generatedCardGroup(origin: string | undefined): GeneratedCardGroup | null {
  if (!origin || !Object.hasOwn(GENERATED_CARD_ORIGIN_GROUPS, origin)) return null
  return GENERATED_CARD_ORIGIN_GROUPS[origin as GeneratedCardOrigin]
}

/**
 * Whether a content origin is a generated post whose author is a system
 * creator (a persona), which viewers can never follow.
 */
export function isGeneratedCardOrigin(origin: string | undefined): boolean {
  return generatedCardGroup(origin) !== null
}

/** The origin's header badge label, or '' when it shows none. */
export function generatedCardBadgeText(branding: BrandingConfig, origin: string | undefined): string {
  const group = generatedCardGroup(origin)
  if (!group) return ''
  const text = branding.components[group]?.badgeText
  return typeof text === 'string' ? text.trim() : ''
}

/** The author a card shows: a creator's name and avatar, or a generated origin's persona override. */
export interface GeneratedCardPersona {
  display_name: string
  avatar_url: string | null
}

/**
 * The author name and avatar a card of this origin shows: each non-empty
 * persona token (trimmed) replaces the creator's value; other origins and
 * empty tokens keep the creator's. Only the displayed author changes, never
 * the creator's id, follow state or CTA target.
 */
export function generatedCardPersona(
  branding: BrandingConfig,
  origin: string | undefined,
  creator: { display_name?: string | null, name?: string | null, avatar_url?: string | null } | null | undefined,
): GeneratedCardPersona {
  const persona: GeneratedCardPersona = {
    display_name: creator?.display_name || creator?.name || '',
    avatar_url: creator?.avatar_url || null,
  }
  const group = generatedCardGroup(origin)
  if (!group) return persona
  const tokens = branding.components[group]
  const name = typeof tokens?.personaName === 'string' ? tokens.personaName.trim() : ''
  const avatar = typeof tokens?.personaAvatarUrl === 'string' ? tokens.personaAvatarUrl.trim() : ''
  if (name) persona.display_name = name
  if (avatar) persona.avatar_url = avatar
  return persona
}

/** Inline CSS variables for a standard card of a generated origin; {} for other origins or an all-empty group. */
export function generatedCardOriginStyle(branding: BrandingConfig, origin: string | undefined): Record<string, string> {
  const group = generatedCardGroup(origin)
  if (!group) return {}
  const tokens = branding.components[group]
  if (!tokens) return {}
  const style: Record<string, string> = {}
  for (const key of GENERATED_CARD_TOKEN_KEYS) {
    const value = tokens[key]
    if (typeof value !== 'string' || value.trim() === '') continue
    const targets = GENERATED_CARD_STYLE_VARS[key]
    for (const cssVar of targets === 'group' ? [componentVarName(group, key)] : targets) style[cssVar] = value
  }
  return style
}

export function applyBrandingCssVariables(branding: BrandingConfig, container: HTMLElement) {
  const vars = brandingToCssVars(branding)
  for (const [name, value] of Object.entries(vars)) {
    container.style.setProperty(name, value)
  }
}

export function cloneDefaultBranding(): BrandingConfig {
  return deepClone(DEFAULT_BRANDING)
}
